/*
 * ez-model-switch: the product gallery's Frame / On model switch (thread t011, 2026-10-21).
 * Markup and CSS: snippets/ez-model-switch.liquid, placed inside the gallery's
 * <slideshow-container>. Memory: storefront/memory.md, "The gallery's Frame / On model switch".
 *
 * Contract.
 * Reads, from the page, each time the element connects and on refresh():
 *   - the slides of the <slideshow-component> it sits in. The gallery holds only the selected
 *     colour's images (the theme's colour-by-alt rule), so what it finds is that colour's;
 *   - each image slide's img alt: an alt containing " worn by " (any case) is the on-model shot;
 *   - data-product-id, data-variant-id, and data-design-mode (the theme editor).
 * Does:
 *   - shows itself only when the slides hold an on-model shot and at least one other image;
 *     in the theme editor always, where On model with no shot does nothing;
 *   - Frame selects the first slide that isn't an on-model shot, On model the first shot, through
 *     the slideshow's own select();
 *   - follows the slideshow's slideshow:select events (swipe, arrows, thumbnails, zoom), so the
 *     pressed side is On model while a shot shows and Frame anywhere else;
 *   - hides itself as soon as its section's variant picker changes. Horizon's media-gallery then
 *     replaces the whole gallery with the new variant's, and the new switch starts on Frame on the
 *     colour's first image; if the gallery is not replaced, variant:update shows it again;
 *   - sends GA4 on_model_switch {to: 'on_model' | 'frame', product_id, variant_id} when a tap
 *     moves the gallery, only if gtag is a function, inside try.
 * window.EzModelSwitch.refresh() re-reads every switch on the page (to test on a changed page).
 */
const MARK = ' worn by ';

/** @param {Element | null | undefined} slide */
function isShot(slide) {
  if (!slide || !slide.classList.contains('product-media-container--image')) return false;
  const img = slide.querySelector('img.product-media__image:not(.ez-layer)') || slide.querySelector('img');
  return !!img && (img.getAttribute('alt') || '').toLowerCase().includes(MARK);
}

/**
 * @param {'on_model' | 'frame'} to
 * @param {DOMStringMap} data
 */
function track(to, data) {
  try {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'on_model_switch', {
      to,
      product_id: data.productId || '',
      variant_id: data.variantId || '',
    });
  } catch (error) {
    /* a tracking failure never breaks the gallery */
  }
}

class EzModelSwitch extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {any} the gallery's <slideshow-component> */
  #slideshow = null;
  /** @type {Element | null} */
  #model = null;
  /** @type {Element | null} */
  #frame = null;
  #selects = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#slideshow = this.closest('slideshow-component');

    this.addEventListener('click', this.#onClick, { signal });
    this.#slideshow?.addEventListener('slideshow:select', this.#onSelect, { signal });
    document.addEventListener('change', this.#onPickerChange, { signal, capture: true });
    document.addEventListener('variant:update', () => this.refresh(), { signal });

    this.refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** The slides in the order the gallery shows them. */
  #slides() {
    const slideshow = this.#slideshow;
    if (!slideshow) return [];
    if (Array.isArray(slideshow.slides)) return slideshow.slides;
    return [...slideshow.querySelectorAll('slideshow-slide')].filter((slide) => !slide.hasAttribute('hidden'));
  }

  #currentSlide() {
    const slideshow = this.#slideshow;
    const slides = this.#slides();
    if (slideshow && typeof slideshow.current === 'number' && slides[slideshow.current]) {
      return slides[slideshow.current];
    }
    return slides[0] || null;
  }

  /** Re-reads the slides and shows, hides and sets the switch from them. */
  refresh() {
    if (!this.isConnected) return;
    const slides = this.#slides().filter((slide) => slide.classList.contains('product-media-container'));
    this.#model = slides.find((slide) => isShot(slide)) || null;
    this.#frame = slides.find((slide) => !isShot(slide)) || null;

    const usable = !!(this.#model && this.#frame);
    this.hidden = !(usable || this.hasAttribute('data-design-mode'));
    this.#press(isShot(this.#currentSlide()));
  }

  /** @param {boolean} onModel */
  #press(onModel) {
    for (const button of this.querySelectorAll('button[data-side]')) {
      const pressed = (button.getAttribute('data-side') === 'on_model') === onModel;
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }
  }

  /** @param {Event} event */
  #onSelect = (event) => {
    if (event.target !== this.#slideshow) return;
    this.#selects += 1;
    const slide = /** @type {CustomEvent} */ (event).detail?.slide;
    this.#press(isShot(slide));
  };

  /** @param {Event} event */
  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-side]') : null;
    if (!button || !this.contains(button)) return;

    const toModel = button.getAttribute('data-side') === 'on_model';
    const target = toModel ? this.#model : this.#frame;
    const slideshow = this.#slideshow;
    if (!target || !slideshow || typeof slideshow.select !== 'function') return;

    const index = this.#slides().indexOf(target);
    if (index < 0 || this.#currentSlide() === target) return;

    this.#press(toModel);
    const before = this.#selects;
    try {
      await slideshow.select(index, event);
    } catch (error) {
      /* the slideshow's own failure: fall through and read where it stands */
    }

    if (this.#selects === before) {
      // The slideshow didn't move (busy or not ready): show where it really is.
      this.#press(isShot(this.#currentSlide()));
      return;
    }
    track(toModel ? 'on_model' : 'frame', this.dataset);
  };

  /** @param {Event} event */
  #onPickerChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('variant-picker')) return;
    const section = this.closest('.shopify-section');
    if (!section || !section.contains(target)) return;
    this.hidden = true;
  };
}

if (!customElements.get('ez-model-switch')) {
  customElements.define('ez-model-switch', EzModelSwitch);
}

window.EzModelSwitch = {
  refresh() {
    document.querySelectorAll('ez-model-switch').forEach((element) => {
      if (element instanceof EzModelSwitch) element.refresh();
    });
  },
};
